import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PContent from "../../../../../atoms/text/PContent";
import FancyAnimatePoint from "../../../../../atoms/button/FancyAnimatePoint";
import Button from "../../../../../atoms/button/Button";
import MobileSubmenu from "./MobileSubmenu";
import type { MobileMenuItem } from "../types/mobileNavigation";
import { goToEntryPricingSection } from "../../goToEntryPricingSection";

interface MobileMenuNavigationProps {
  menuItems: MobileMenuItem[];
  activeSubmenu: string | null;
  onSubmenuToggle: (menuKey: string, hasSubmenu: boolean) => void;
  contactAction?: () => void;
  contactText?: string;
}

const MobileMenuNavigation: React.FC<MobileMenuNavigationProps> = ({
  menuItems,
  activeSubmenu,
  onSubmenuToggle,
  contactAction,
  contactText = "Entrer Dans l'App",
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const hasSubmenu = (item: MobileMenuItem) => Boolean(item.submenu && item.submenu.length > 0);

  const handleContactClick = () => {
    if (contactAction) {
      contactAction();
    } else {
      goToEntryPricingSection(pathname, navigate);
    }
  };

  const handleMenuItemClick = (item: MobileMenuItem) => {
    if (hasSubmenu(item)) {
      onSubmenuToggle(item.key, true);
      return;
    }
    if (item.onClick) {
      item.onClick();
    }
    if (item.href) {
      navigate(item.href);
    }
    onSubmenuToggle(item.key, false);
  };

  return (
    <nav className="mt-1 flex w-full flex-col space-y-3 rounded-bl-[50px] rounded-br-[50px] border-b-[2.5px] border-l-[2.5px] border-r-[2.5px] border-t-[2.5px] border-muted bg-white p-4">
      {menuItems.map((item) => (
        <div key={item.key}>
          <button
            type="button"
            onClick={() => handleMenuItemClick(item)}
            className={`flex w-full items-center justify-between rounded-lg p-3 text-left font-medium transition-all duration-200 hover:bg-gray-50 ${
              activeSubmenu === item.key ? "bg-blue-50 text-primary" : "text-gray-700"
            }`}
          >
            <div className="flex items-center space-x-2">
              <PContent className="font-bold">{item.name}</PContent>
              {hasSubmenu(item) && <FancyAnimatePoint size={5} oscillate />}
            </div>

            {hasSubmenu(item) && (
              <svg
                className={`h-4 w-4 transition-transform duration-200 ${
                  activeSubmenu === item.key ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>

          {hasSubmenu(item) && (
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                activeSubmenu === item.key ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              {activeSubmenu === item.key && item.submenu && (
                <MobileSubmenu items={item.submenu} />
              )}
            </div>
          )}
        </div>
      ))}

      <div>
        <Button type="button" expandWidth onClick={handleContactClick} className="!mt-0 w-full">
          {contactText}
        </Button>
      </div>
    </nav>
  );
};

export default MobileMenuNavigation;
